import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import { ModalSize } from "@via/utils/constants.tsx";
import Modal from "@via/components/modal.tsx";
import { useCreateDataSource } from "@via/pages/data/data-connections/data-connections.api.tsx";
import {
  CreateDataSourceRequestBody,
  CredentialView,
  DataSourceProvider,
  Permissions,
} from "common";
import { useGetSavedCredentials } from "@via/pages/account/profile/profile.api.tsx";
import { isAgentEnvironment } from "@via/pages/data/data-sources/ssh-data-source/agent-listing-notices.ts";
import { useDataRoots } from "@via/pages/account/profile/run-environments/data-roots.api.tsx";
import { writableToggleView } from "@via/pages/data/data-sources/ssh-data-source/writable-folder.ts";
import { classNames } from "@via/utils/helpers.tsx";

interface SaveCurrentDataSourceProps {
  dataSourceProvider: DataSourceProvider;
  credentialId?: number;
  path: string;
  open: boolean;
  setOpen: (value: boolean) => void;
}

interface SaveDataSourceForm {
  name: string;
  path: string;
  writable: boolean;
}

const SaveCurrentDataSource: React.FC<SaveCurrentDataSourceProps> = ({
  dataSourceProvider,
  credentialId,
  path,
  open,
  setOpen,
}) => {
  const { control, handleSubmit, watch, setValue } =
    useForm<SaveDataSourceForm>({
      mode: "onChange",
      defaultValues: {
        name: "",
        path: path,
        writable: false,
      },
    });

  // agent-5-5 (spec 7): on an agent environment a bookmark may be writable, only under a read and
  // write data folder; the reason is shown otherwise.
  const { data: savedCredentials } = useGetSavedCredentials({
    type: CredentialView.UserOwned,
  });
  const onAgent =
    dataSourceProvider === DataSourceProvider.SSH &&
    isAgentEnvironment(savedCredentials, credentialId);
  const {
    data: dataRoots,
    isLoading: dataRootsLoading,
    isError: dataRootsUnavailable,
  } = useDataRoots(onAgent ? credentialId : undefined);
  const writableView = writableToggleView({
    onAgent,
    path: watch("path"),
    roots: dataRoots,
    rootsLoading: onAgent && dataRootsLoading,
    rootsUnavailable: dataRootsUnavailable,
  });

  useEffect(() => {
    if (!writableView.enabled) setValue("writable", false);
  }, [writableView.enabled]);

  const {
    mutateAsync: createDataSource,
    isPending: isCreateDataSourceLoading,
    isSuccess: isCreateDataSourceSuccess,
  } = useCreateDataSource();

  const handleSaveCurrentDataSource = (payload: SaveDataSourceForm) => {
    const { writable, ...fields } = payload;
    const createDataSourceParams: CreateDataSourceRequestBody = {
      provider: dataSourceProvider,
      permission: Permissions.UserOwned,
      ...fields,
      ...(writableView.enabled && writable ? { writable: true } : {}),
    };
    credentialId && (createDataSourceParams.credentialId = credentialId);
    createDataSource(createDataSourceParams);
  };

  useEffect(() => {
    if (isCreateDataSourceSuccess) {
      setOpen(false);
    }
  }, [isCreateDataSourceSuccess]);

  const handleCancelClick = () => {
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      onCancel={() => setOpen(false)}
      title="Bookmark Current Datasource"
      showButtons={false}
      size={ModalSize.Large}
      width={800}>
      <div>
        <form
          onSubmit={e => {
            e.preventDefault();
            void handleSubmit(handleSaveCurrentDataSource)(e);
          }}>
          <FormInputComponent
            control={control}
            name={"name"}
            label={"Bookmark Name"}
            showRequiredField={true}
          />

          <div
            className={classNames(
              dataSourceProvider === DataSourceProvider.BaseSpace
                ? "hidden"
                : ""
            )}>
            <FormInputComponent
              control={control}
              name={"path"}
              label={"Path"}
              showRequiredField={true}
            />
          </div>

          {writableView.visible ? (
            <div className="mt-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  data-testid="save-data-source-writable"
                  disabled={!writableView.enabled}
                  checked={watch("writable")}
                  onChange={event => setValue("writable", event.target.checked)}
                />
                Writable: allow uploads into this data source
              </label>
              {writableView.reason ? (
                <p
                  data-testid="save-data-source-writable-reason"
                  className="mt-1 text-xs text-gray-500">
                  {writableView.reason}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="text-xs font-semibold text-gray-400 mt-2">
            Bookmark this location details as a datasource for quick access.
          </div>

          <div className="flex flex-shrink-0 justify-end pt-4 gap-4">
            <button
              id="cancel-save-data-source-button"
              type="button"
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              onClick={handleCancelClick}>
              Cancel
            </button>
            <button
              id="save-data-source-button"
              type="submit"
              disabled={isCreateDataSourceLoading}
              className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              <Spinner loading={isCreateDataSourceLoading}>
                Bookmark Datasource
              </Spinner>
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default SaveCurrentDataSource;
